import { CircularProgress, FormControl, Grid, MenuItem, Typography } from '@material-ui/core/';
import upperFirst from 'lodash/upperFirst';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openDialog } from '~/store/legacy/actions/dialogActions';
import {
  getPaymentMethods,
  resetPaymentError,
  choosePaymentMethod,
} from '~/store/legacy/actions/paymentActions';
import AlipayLogo from '~/assets/alipay-logo.png';
import { CLIENT_TYPES } from '~/constant';
import { useAppConfig } from '~/hooks/useAppConfig.js';
import { useAlipay } from '~/hooks/useAlipay.js';
import { selectPayment } from '~/store/legacy/selector/paymentSelectors';
import {
  selectIsUSAUser,
  selectIsUserLoggedIn,
  selectUserId,
} from '~/store/legacy/selector/userSelectors';
import ControlledSelect from '../ui-kit/ControlledSelect/ControlledSelect';
import ErrorMessage from '../ui-kit/ErrorMessage/ErrorMessage';
import Subtitle from '../ui-kit/Subtitle/Subtitle';
import CardDialog from './CardDialog';
import InputSectionLayout from './InputSectionLayout';
import PaymentOption from './PaymentOption';
import useStyles from './PaymentSection.style';
import { useStripe } from '~/hooks/useStripe';

const { ALIPAY: ALIPAY_IDENTIFIER } = CLIENT_TYPES;

const Payment = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { isStripeReady } = useStripe();
  // Todo: UI needs to force Alipay as the payment option https://chowbus.atlassian.net/browse/DW-517
  const appConfig = useAppConfig();
  const isAlipay = appConfig.isAlipay;
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const userId = useSelector(selectUserId);
  const isUSAUser = useSelector(selectIsUSAUser(appConfig.serviceRegions));
  const { loaded, selectedMethod, methods, errorMessage } = useSelector(selectPayment);
  const [selectedOption, setSelectedOption] = useState({ identifier: '' });
  const [isSelectedMethodAlipay, setIsSelectedMethodAlipay] = useState(false);

  useAlipay();
  useEffect(() => {
    if (isUserLoggedIn && isStripeReady) {
      dispatch(getPaymentMethods(isAlipay, userId));
    }
  }, [isStripeReady]);

  useEffect(() => {
    if (isAlipay) {
      handleSelectAlipay();
    }
  }, [isAlipay]);

  useEffect(() => {
    if (selectedMethod) {
      const isAlipaySelected = selectedMethod && selectedMethod.identifier === ALIPAY_IDENTIFIER;
      setIsSelectedMethodAlipay(isAlipaySelected);
      if (!isAlipaySelected) {
        setSelectedOption(selectedMethod);
      } else {
        setSelectedOption({ identifier: '' });
      }
    } else {
      setSelectedOption({ identifier: '' });
    }
  }, [selectedMethod]);

  const formatCard = (card) => {
    const { card_last_4, card_type } = card;
    return `${upperFirst(card_type)} - ${card_last_4}`;
  };

  const openCardDialog = () => {
    if (errorMessage) {
      dispatch(resetPaymentError());
    }
    dispatch(
      openDialog({
        body: <CardDialog isUSAUser={isUSAUser} isAlipay={isAlipay} />,
      })
    );
  };

  const handleSelectSavedCard = (e) => {
    const { value } = e.target;
    const selectedMethod = methods.find((method) => method.identifier === value);
    handleSelectMethod(selectedMethod);
  };

  const handleSelectMethod = (method) => {
    dispatch(choosePaymentMethod(method));
  };

  const handleSelectAlipay = () => {
    handleSelectMethod({
      identifier: ALIPAY_IDENTIFIER,
      processor: ALIPAY_IDENTIFIER,
    });
  };

  const renderPaymentMethods = () => {
    return (
      <>
        {!!errorMessage && <ErrorMessage message={errorMessage} />}
        {!isAlipay && !!methods.length && (
          <FormControl variant='outlined' fullWidth className={classes.formControl}>
            <ControlledSelect
              value={selectedOption?.identifier || ''}
              onChange={handleSelectSavedCard}
              inputProps={{
                name: 'paymentCC',
                id: 'paymentCC',
              }}
              displayEmpty
              isSelected={!isSelectedMethodAlipay}
              disabled={methods.length < 2 && !isSelectedMethodAlipay}
              classes={{ disabled: classes.disabledSelect }}
              controlLabel={selectedOption.identifier ? 'Choose another card' : 'Choose a card'}>
              {methods.map((method) => (
                <MenuItem key={method.identifier} value={method.identifier}>
                  <Grid container alignItems='center'>
                    <Typography data-loacator='payment-cc'>{formatCard(method)}</Typography>
                  </Grid>
                </MenuItem>
              ))}
              <MenuItem value='' disabled>
                <Grid container alignItems='center'>
                  <Typography data-loacator='payment-cc-none'>None</Typography>
                </Grid>
              </MenuItem>
            </ControlledSelect>
          </FormControl>
        )}
        <Grid container spacing={2}>
          <Grid item sm={6} xs={12}>
            <PaymentOption
              label='Alipay'
              logoSrc={AlipayLogo}
              logoAlt='alipay logo'
              onClick={handleSelectAlipay}
              selected={isSelectedMethodAlipay}
              data-locator='alipay-payment-option'
            />
          </Grid>
          {!isAlipay && (
            <Grid item sm={6} xs={12}>
              {isStripeReady ? (
                <PaymentOption
                  label='Use New Credit/Debit Card'
                  onClick={openCardDialog}
                  data-locator='new-card-payment-option'
                />
              ) : (
                <CircularProgress />
              )}
            </Grid>
          )}
        </Grid>
      </>
    );
  };

  return (
    <Grid container className={classes.field}>
      <Subtitle hLevel={6} bottomMargin>
        Payment Method
      </Subtitle>
      <Grid container>
        {!loaded ? (
          <Grid item container xs={12} justify='center'>
            <Grid item>
              <CircularProgress data-locator='payment-loader' />
            </Grid>
          </Grid>
        ) : (
          <InputSectionLayout left={renderPaymentMethods()}></InputSectionLayout>
        )}
      </Grid>
    </Grid>
  );
};

export default Payment;
